import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import ApiResponse from 'models/api-response/api-response.model';
import Log from 'models/log/log.model';
import OrganizationUnit from 'models/organization-unit/organization-unit.model';
import Project from 'models/project/project.model';
import ProjectStat from 'models/project-stat/project-stat.model';
import ProjectPermission from 'models/project-permission/project-permission.model';
import User from 'models/user/user.model';
import { LogService } from 'services/log/log.service';
import { UserService } from 'services/user/user.service';
import { ApiRequestService } from 'services/api-request/api-request.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private selectedInstance: BehaviorSubject<Project> = new BehaviorSubject(new Project());
  private selectedPermissions: BehaviorSubject<ProjectPermission> = new BehaviorSubject(new ProjectPermission());
  private selectedUsers: BehaviorSubject<User[]> = new BehaviorSubject([]);

  constructor(
    public apiRequestService: ApiRequestService,
    public userService: UserService,
    public logService: LogService,
  ) { }

  public async create(instance: Project): Promise<Project> {
    return new Promise((resolve: (r: Project) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post(
        '/project/create',
        {
          name: instance.name,
          organizationUnit: instance.organizationUnit.name
        },
      ).subscribe({
        next: async (res: ApiResponse<Project>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.create',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new Project(res.data));
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.create',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async findByOu(ou: OrganizationUnit): Promise<Project[]> {
    return new Promise((resolve: (r: Project[]) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get(`/project/findbyou/${ou.id}`).subscribe({
        next: async (res: ApiResponse<Project[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.findByOu',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          const resArr: Project[] = [];
          res.data.forEach(element => resArr.push(new Project(element)));
          return resolve(resArr);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.findByOu',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async get(id: string): Promise<Project> {
    return new Promise((resolve: (r: Project) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get(`/project/get/${id}`).subscribe({
        next: async (res: ApiResponse<Project>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.get',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new Project(res.data));
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.get',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async stat(instance: Project, user: User, from: Date, to: Date): Promise<ProjectStat> {
    return new Promise((resolve: (r: ProjectStat) => any, reject: (e: Error) => any) => {
      let url: string = `/project/stat?id=${instance.id}${user.username.length > 0 ? `&user=${user.username}` : ''}${from ? `&from=${String(from.getTime())}` : ''}${to ? `&to=${String(to.getTime())}` : ''}`;

      this.apiRequestService.get(url).subscribe({
        next: async (res: ApiResponse<ProjectStat>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.stat',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new ProjectStat(res.data));
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.stat',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async select(id: string): Promise<Project> {
    return new Promise(async (resolve: (r: Project) => any) => {
      const instance: Project = await this.get(id);
      this.selectedInstance.next(instance);
      const currentUser: User = this.userService.getCurrentUser().value;
      if (!currentUser.isSuperAdminIn(instance.organizationUnit.name)) {
        const perms: ProjectPermission = await this.getPerms(instance, currentUser);
        this.selectedPermissions.next(perms);
      }
      const userList = await this.userList(instance);
      this.selectedUsers.next(userList);
      return resolve(instance);
    });
  }

  public async userList(instance: Project): Promise<User[]> {
    return new Promise((resolve: (r: User[]) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get(`/project/userlist/${instance.id}`).subscribe({
        next: async (res: ApiResponse<User[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.userList',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          const resArr: User[] = [];
          res.data.forEach(u => resArr.push(new User(u)));
          return resolve(resArr);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.userList',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async update(instance: Project): Promise<Project> {
    return new Promise((resolve: (r: Project) => any, reject: (e: Error) => any) => {
      this.apiRequestService.put(
        '/project/',
        {
          id: instance.id,
          name: instance.name,
          // organizationUnit: instance.organizationUnit.name,
        },
      ).subscribe({
        next: async (res: ApiResponse<Project>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.update',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new Project(res.data));
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.update',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async delete(instance: Project): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.delete(`/project/${instance.id}`, {}).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.delete',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.delete',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async updatePerms(projectPermission: ProjectPermission): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.put(
        '/project/updateperms/',
        {
          id: projectPermission.project.id,
          username: projectPermission.user.username,
          projectPermission: {
            manageUsers: projectPermission.manageUsers,
            update: projectPermission.update,
            delete: projectPermission.delete,
            updateSchema: projectPermission.updateSchema,
            updateLayers: projectPermission.updateLayers,
            updateArcgis: projectPermission.updateArcgis,
            updateTickets: projectPermission.updateTickets,
          },
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.updatePerms',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.updatePerms',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async addUser(instance: Project, user: User): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post(
        '/project/adduser/',
        {
          id: instance.id,
          username: user.username,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.addUser',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.addUser',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async removeUser(instance: Project, user: User): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post(
        '/project/removeuser/',
        {
          id: instance.id,
          username: user.username,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.removeUser',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.removeUser',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async getPerms(instance: Project, user: User): Promise<ProjectPermission> {
    return new Promise((resolve: (r: ProjectPermission) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get(`/project/perms/${instance.id}/${user.username}`).subscribe({
        next: async (res: ApiResponse<ProjectPermission>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.getPerms',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          const perms: ProjectPermission = new ProjectPermission(res.data);
          this.selectedPermissions.next(perms);
          return resolve(perms);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.getPerms',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async addLayer(project: Project, layerUrls: string[]) {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post(
        '/project/addlayer',
        {
          project: project.id,
          externalLayers: layerUrls,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.addLayer',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.addLayer',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async removeLayer(project: Project, layerUrl: string) {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post(
        '/project/removelayer',
        {
          project: project.id,
          externalLayer: layerUrl,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.removeLayer',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.removeLayer',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async moveLayerUp(project: Project, layerUrl: string) {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.put(
        '/project/layerup',
        {
          id: project.id,
          url: layerUrl,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.moveLayerUp',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.moveLayerUp',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async moveLayerDown(project: Project, layerUrl: string) {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.put(
        '/project/layerdown',
        {
          id: project.id,
          url: layerUrl,
        },
      ).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: `requestError: ${JSON.stringify(res)}`,
              event: 'serviceError',
              performer: 'ProjectService.moveLayerDown',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async (error) => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ProjectService.moveLayerDown',
            type: 'error',
          }));
          return reject(new Error('requestError'));
        },
      });
    });
  }

  public async hasSettingsButton(project: Project, user: User, perms: ProjectPermission): Promise<boolean> {
    return new Promise(async(resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      if (await this.canUpdateSchema(project, user, perms)) {
        return resolve(true);
      }
      if (await this.canManageUsers(project, user, perms)) {
        return resolve(true);
      }
      if (await this.canUpdateLayers(project, user, perms)) {
        return resolve(true);
      }
      if (await this.canUpdate(project, user, perms)) {
        return resolve(true);
      }
      if (await this.canDeleteProject(project, user, perms)) {
        return resolve(true);
      }
      if (await this.canUpdateArcgis(project, user, perms)) {
        return resolve(true);
      }
      return resolve(false);
    });
  }

  public async canWorkOn(project: Project, user: User, perms: ProjectPermission): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      if (user.isSuperAdminIn(project.id)) {
        return resolve(true);
      }
      return resolve(perms.workOn > 0);
    });
  }

  public async canUpdate(project: Project, user: User, perms: ProjectPermission): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      console.log("update perms:", perms.update > 0);
      return resolve(perms.update > 0);
    });
  }

  public async canUpdateSchema(project: Project, user: User, perms: ProjectPermission): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      if (user.isSuperAdminIn(project.id)) {
        return resolve(true);
      }
      return resolve(perms.updateSchema > 0);
    });
  }

  public canManageUsers(project: Project, user: User, perms: ProjectPermission): boolean {
    if (user.isSuperAdminIn(project.organizationUnit.name)) {
      return true;
    }
    return perms.manageUsers > 0;
  }

  public canUpdateLayers(project: Project, user: User, perms: ProjectPermission): boolean {
    if (user.isSuperAdminIn(project.organizationUnit.name)) {
      return true;
    }
    return perms.updateLayers > 0;
  }

  public canDeleteProject(project: Project, user: User, perms: ProjectPermission): boolean {
    if (user.isSuperAdminIn(project.organizationUnit.name)) {
      return true;
    }
    return perms.delete > 0;
  }

  public canUpdateArcgis(project: Project, user: User, perms: ProjectPermission): boolean {
    if (user.isSuperAdminIn(project.organizationUnit.name)) {
      return true;
    }
    return perms.updateArcgis > 0;
  }

  public async getFullList() {
  }

  public getSelectedInstance() {
    return this.selectedInstance;
  }

  public getSelectedPermissions() {
    return this.selectedPermissions;
  }

  public getSelectedUsers() {
    return this.selectedUsers;
  }
}
